import { Dropdown, Icon, Portal } from "semantic-ui-react";

import React, { useCallback, useState } from "react";
import useEvent from "react-use/lib/useEvent";

import AlertModal from "../AlertModal/AlertModal";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";

import DropdownMenuItem from "./DropdownMenuItem";

export default function DropdownPortal({
	icon = "ellipsis vertical",
	direction = "left",
	loading: initialLoading = false,
	maxHeight = "80vh",
	children,
	text,
	options,
	size,
	style,
	fitted,
	iconStyle,
	menuStyle,
	onChange,
	...props
}) {
	const [open, setOpen] = useState(false);
	const [error, setError] = useState(null);
	const [loading, setLoading] = useState(false);
	const [confirm, setConfirm] = useState(null);
	const [{ x, y }, setPosition] = useState({ x: 0, y: 0 });

	const isLoading = loading || initialLoading;

	const onScroll = useCallback(() => {
		setOpen(false);
	}, []);

	useEvent("scroll", open && onScroll);

	return (
		<>
			<Portal
				open={open}
				trigger={
					<Dropdown
						floating
						scrolling
						icon={text ? icon : false}
						direction={direction}
						open={open}
						onOpen={(e) => {
							setPosition({
								x: e.pageX - window.scrollX,
								y: e.pageY - window.scrollY,
							});

							setOpen(true);
						}}
						closeOnBlur={false}
						onClose={() => setOpen(false)}
						trigger={
							!text &&
							(typeof icon === "string" ? (
								<Icon
									style={iconStyle}
									size={size}
									fitted={fitted}
									loading={isLoading}
									name={isLoading ? "spinner" : icon}
									onClick={(e) => e.preventDefault()}
								/>
							) : (
								icon
							))
						}
						text={
							text && (
								<div
									style={{
										overflow: "hidden",
										whiteSpace: "nowrap",
										textOverflow: "ellipsis",
									}}
								>
									{text}
								</div>
							)
						}
						style={style}
						{...props}
					>
						<div />
					</Dropdown>
				}
			>
				<Dropdown
					open
					icon={false}
					floating
					scrolling
					direction={direction}
					onClose={() => setOpen(false)}
					style={{
						position: "fixed",
						top: y,
						left: x,
						zIndex: 1000,
					}}
					{...props}
				>
					<Dropdown.Menu
						style={{
							maxHeight,
							...menuStyle,
						}}
					>
						{typeof children === "function" ? children({ setLoading, setError }) : children}

						{options
							?.filter((option) => option && !option.hidden)
							.map(({ type, action, onClick, confirmText, ...props }, index) => {
								if (type === "divider") {
									return <Dropdown.Divider key={index} />;
								}

								const clickHandler = action
									? async () => {
											setLoading(true);
											setError(null);

											try {
												await action();
											} catch (error) {
												console.error(error);
												setError(error);
											}

											setLoading(false);
									  }
									: onClick || onChange;

								return (
									<DropdownMenuItem
										key={index}
										onClick={
											confirmText
												? () =>
														setConfirm({
															text: confirmText,
															onConfirm: clickHandler,
														})
												: clickHandler
										}
										{...props}
									/>
								);
							})}
					</Dropdown.Menu>
				</Dropdown>
			</Portal>

			{error && <AlertModal text={error.message || error} onClose={() => setError(null)} />}

			{confirm && <ConfirmationModal {...confirm} onClose={() => setConfirm(null)} />}
		</>
	);
}
